import React, {useMemo} from 'react';
import Component from 'components/organisms/Utils/Component/Component';
import {useEffectItem, useUpdatedRef} from 'helpers/hooks/utils';
import Debounce from 'components/organisms/Utils/Debounce/Debounce';
import {withMemo} from 'helpers/wrapper';
import Typography from 'components/atoms/Text/Typography/Typography';
import {getColumnGeometry, getTableGeometry} from 'material-react-table';

// tuned so the practical-max grid lands at 10 frames of full paint: 24 rows x 24 cols with 3
// pinned is ceil(3*24/64)=2 pinned batches + ceil(21*24/64)=8 body batches = 10. trade is a
// fuller frame (~64 cells) against fewer frames to fully painted - here we optimise the latter
const CELLS_PER_FRAME = 64;

const BAYER_LEVELS = 64;

// ordered dither - the checkerboard generalised to N levels, so every frame paints cells
// spread over the whole viewport instead of a slab of adjacent columns. 8x8 because the
// levels cap the frames: a 4x4 has 16, and asking it for more clumps them back together
const BAYER = [
  [0, 32, 8, 40, 2, 34, 10, 42],
  [48, 16, 56, 24, 50, 18, 58, 26],
  [12, 44, 4, 36, 14, 46, 6, 38],
  [60, 28, 52, 20, 62, 30, 54, 22],
  [3, 35, 11, 43, 1, 33, 9, 41],
  [51, 19, 59, 27, 49, 17, 57, 25],
  [15, 47, 7, 39, 13, 45, 5, 37],
  [63, 31, 55, 23, 61, 29, 53, 21]
];

const getFrame = (rowIndex, columnIndex, batches) => {
  return Math.floor((BAYER[rowIndex % 8][columnIndex % 8] * batches) / BAYER_LEVELS);
}

const getCellFrames = (props) => {
  const table = props.table.current;
  const geometry = getColumnGeometry(table, props.column.current);
  const rowIndex = props.row.current?.index ?? 0;
  const rows = table.rowRenderCount ?? 0;
  const columns = table.columnRenderCount ?? 0;

  // the painted cells, not the totals in state: a tall screen carries as many extra cells per
  // frame as a wide one does, so both dimensions set the number of frames
  const pinnedCount = getTableGeometry(table).pinnedCount ?? 0;
  const pinnedBatches = Math.max(1, Math.ceil((pinnedCount * rows) / CELLS_PER_FRAME));

  if (geometry.pinned) {
    // the pinned columns paint first, dithered over their own set - the row appears sooner
    // without the rest, and on a tall screen they are a slab of their own
    return 1 + getFrame(rowIndex, Math.max(0, geometry.pinnedIndex), pinnedBatches);
  } else {
    const cells = Math.max(0, columns - pinnedCount) * rows;
    const batches = Math.max(1, Math.ceil(cells / CELLS_PER_FRAME));

    return 1 + pinnedBatches + getFrame(rowIndex, Math.max(0, geometry.index), batches);
  }
}

const TableCellContent = withMemo((props) => {
  const debounced = Boolean(props.debounce) && !props.header && !props.edit;

  return <Debounce placeholder={props.loaders ? props.Skeleton : null}
                   frames={debounced ? getCellFrames(props) : 0}>
    <Component Original={props.Cell}
               table={props.table.current}
               column={props.column.current}
               row={props.row.current}
               cell={props.cell.current}
               data={props.data}/>
  </Debounce>
})

const TableCell = (props) => {
  const {
    header,
    table,
    column,
    row,
    cell,
    edit,
    wasEditing
  } = props;

  const key = header ? `${column.id}` : `${column.id}_${row.id}`;
  const tableRef = useUpdatedRef(table);
  const columnRef = useUpdatedRef(column);
  const rowRef = useUpdatedRef(row);
  const cellRef = useUpdatedRef(cell);

  const Cell = useEffectItem(header ? column.columnDef.DefaultHeader : (
    edit ? column.columnDef.DefaultEdit : column.columnDef.DefaultCell
  ));
  const isLoading = Boolean(tableRef.current.getState().customIsLoading);
  const debounce = table.getState().debounce && !wasEditing;
  const loaders = table.getState().loaders;

  const size = Math.floor(column.getSize() / 16);
  const skeleton = useMemo(() => {
    const netSize = (size * 16) - 32; // minus padding
    const min = Math.max(2, Math.floor((netSize - (netSize / 3)) / 8));
    const max = Math.max(4, Math.floor(netSize / 8));
    return <Typography key={`skeleton_${min}_${max}`}
                       isLoading={true}
                       animation={false}
                       variant="span"
                       min={min}
                       max={max}/>
  }, [size]);

  if (!header && !edit && isLoading) {
    return skeleton;
  } else if (Cell) {
    return <TableCellContent key={key}
                             data={rowRef.current?.original}
                             Cell={Cell}
                             Skeleton={skeleton}
                             header={header}
                             edit={edit}
                             debounce={debounce}
                             loaders={loaders}
                             table={tableRef}
                             column={columnRef}
                             row={rowRef}
                             cell={cellRef}/>
  }
}

export default TableCell;
